import React, { forwardRef, useEffect, useState, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import cn from 'classnames'
import { utils } from '@grammarly/badger-ui'

import { InputValidationType, TypeComponentTextInput } from '../../lib/types/component-text-input'
import { useUserService } from '../../lib/hooks'
import { ContentProps } from '../renderer'
import { ErrorText, FormControl, Input, Label } from '../ui-lib/form'
import { TextLimitConfig, isValidEmail, hasRestrictedValue } from './validations'
import { HelperText } from '../ui-lib/form'
import { InputError, useError, useValidation, useTextLimitConfig } from './hooks'
import styles from '../ui-lib/form/form.module.scss'
import { conditionalCheck } from './conditional-check'
import { useFormOverrideContext } from '@contexts/form-override-context'

const { mergeRefs } = utils

const validations: Record<InputValidationType, (val: string) => boolean | string> = {
  [InputValidationType.Email]: (val) => isValidEmail(val) || InputError.InvalidEmail,
  [InputValidationType.NeverBounceEmail]: (val) => isValidEmail(val) || InputError.InvalidEmail,
}

interface TextInputProps extends ContentProps<TypeComponentTextInput> {
  labelClassName?: string
  helperTextClassName?: string
  hasLongformText?: boolean
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onPaste?: React.ClipboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  hideErrorText?: boolean
  isDisabled?: boolean
}

export const TextInput = forwardRef(
  (
    {
      structure,
      className,
      labelClassName,
      helperTextClassName,
      hasLongformText: hasLongformTextProps,
      onFocus,
      onBlur,
      onPaste,
      onChange,
      hideErrorText,
      isDisabled,
    }: TextInputProps,
    ref
  ) => {
    const { overrideFields } = useFormOverrideContext()
    const overrideField = overrideFields.find((field) => field.name === structure.fields.name)
    const structureToUse = { ...structure, fields: { ...structure.fields, ...(overrideField ?? {}) } }

    const {
      fields: {
        name,
        placeholder,
        helperText,
        label,
        type,
        isRequired,
        validationType,
        maxLength,
        minCharacterLimit,
        anonymousCharacterLimit,
        useWordLimit,
        conditionalDisplay,
        restrictedValues,
        restrictedValuesErrorMessage,
        isHidden,
        internalName,
        hasLongformText: hasLongformTextStructure,
        hiddenInputValue,
      },
    } = structureToUse

    const hasLongformText =
      typeof hasLongformTextProps !== 'undefined' ? hasLongformTextProps : hasLongformTextStructure

    const { register, watch, setError, clearErrors, resetField } = useFormContext()
    const { isAnonymous } = useUserService().currentUser
    const [neverBounceValidation, setNeverBounceValidation] = useState(true)

    const helperTextId = `${name}-helper-text`

    const textLimitConfig: TextLimitConfig = useTextLimitConfig({
      maxLength,
      hasLongformText,
      minCharacterLimit,
      anonymousCharacterLimit,
      useWordLimit,
    })

    let isConditionalMet = true

    if (conditionalDisplay) {
      const conditionalValues = conditionalDisplay.fields.value
      const conditionalOperator = conditionalDisplay.fields.logicOperator
      const conditionalWatch = watch(conditionalDisplay.fields.formFieldToWatch.fields.name)
      isConditionalMet = conditionalCheck(conditionalOperator, conditionalWatch, conditionalValues)
    }

    const validate = useValidation({
      validationType,
      neverBounceValidation,
      restrictedValues,
      restrictedValuesErrorMessage,
      isAnonymous,
      isConditionalMet,
      textLimitConfig,
      validations,
    })

    const isVisible = isConditionalMet && !isHidden

    const {
      ref: registerRef,
      onBlur: registerOnBlur,
      onChange: registerOnChange,
      ...registerRest
    } = register(name, {
      required: { value: isVisible && !!isRequired, message: InputError.InputRequired },
      validate,
    })

    const error = useError(name, textLimitConfig)

    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      const currentInput = inputRef.current

      const handleNeverBounceStatusChange = (mutationsList: MutationRecord[]) => {
        for (const mutation of mutationsList) {
          if (
            currentInput &&
            mutation.type === 'attributes' &&
            mutation.attributeName === 'data-zi-neverbounce-status'
          ) {
            const neverBounceStatus = (mutation.target as HTMLElement).getAttribute('data-zi-neverbounce-status')
            if (neverBounceStatus === 'invalid' || neverBounceStatus === 'disposable') {
              setError(currentInput.name, { type: 'manual', message: InputError.InvalidEmail })
              setNeverBounceValidation(false)
            } else {
              if (!hasRestrictedValue(currentInput.value, restrictedValues)) {
                clearErrors(currentInput.name)
              }
              setNeverBounceValidation(true)
            }
          }
        }
      }

      const observer = new MutationObserver(handleNeverBounceStatusChange)
      if (currentInput && validationType === InputValidationType.NeverBounceEmail) {
        observer.observe(currentInput, { attributes: true })
      }

      return () => {
        observer.disconnect()
      }
    }, [clearErrors, restrictedValues, setError, validationType])

    useEffect(() => {
      if (!isConditionalMet) {
        resetField(name)
      }
    }, [isConditionalMet, name, resetField])

    const mergedOnBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      registerOnBlur(e)
      onBlur?.(e)
    }

    const mergedOnChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      registerOnChange(e)
      onChange?.(e)
    }

    return (
      <>
        {isConditionalMet ? (
          <FormControl
            name={name}
            isRequired={!isHidden && isRequired}
            isInvalid={!!error}
            error={error}
            isDisabled={isDisabled}
          >
            {label && !isHidden && <Label className={labelClassName}>{label}</Label>}
            {helperText && !isHidden && (
              <HelperText id={helperTextId} className={helperTextClassName}>
                {helperText}
              </HelperText>
            )}
            <Input
              ref={mergeRefs(registerRef, ref, inputRef)}
              as={hasLongformText ? 'textarea' : 'input'}
              placeholder={placeholder}
              type={isHidden ? 'hidden' : type}
              className={cn(className, isHidden && styles.hidden)}
              ariaDescribedBy={helperTextId}
              onPaste={onPaste}
              onFocus={onFocus}
              onBlur={mergedOnBlur}
              data-component-id={internalName}
              onChange={mergedOnChange}
              value={isHidden ? hiddenInputValue : undefined}
              {...registerRest}
            />
            {!hideErrorText && !isHidden && <ErrorText />}
          </FormControl>
        ) : null}
      </>
    )
  }
)
